/**
 * SKU 用户价锁：仅本机生效，不随 source_json 同步商品库
 * key: type_itemid，如 tb-123456 / 1-123456
 */

const STORAGE_KEY = "deliver_sku_user_price_locks_v1";

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch (e) {
    return {};
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store || {}));
  } catch (e) {
    // ignore quota
  }
}

/** 从 skuMap 提取用户锁 { [skuKey]: { calcPrice } } */
export function extractUserPriceLocks(skuMap) {
  const locks = {};
  if (!skuMap || typeof skuMap !== "object") return locks;
  for (const key in skuMap) {
    const sku = skuMap[key];
    if (!sku) continue;
    if (sku.isUserLockPrice && sku.calcPrice != null && sku.calcPrice !== "") {
      locks[key] = { calcPrice: String(sku.calcPrice) };
    }
  }
  return locks;
}

export function saveUserPriceLocks(typeItemId, locks) {
  if (!typeItemId) return;
  const store = readStore();
  if (!locks || Object.keys(locks).length === 0) {
    delete store[typeItemId];
  } else {
    store[typeItemId] = locks;
  }
  writeStore(store);
}

export function loadUserPriceLocks(typeItemId) {
  if (!typeItemId) return {};
  const store = readStore();
  return store[typeItemId] && typeof store[typeItemId] === "object"
    ? store[typeItemId]
    : {};
}

/**
 * 按货源 ID 宽松加载本机锁（兼容 URL 解析差异导致的 type 不一致：0/1/…）
 * TXT 重导后 source_url 可能与编辑时略有不同，不能只认单一 key
 */
export function loadUserPriceLocksByItemId(itemId, sourceUrl) {
  if (itemId == null || itemId === "") return {};
  const store = readStore();
  const idStr = String(itemId);
  const suffix = `-${idStr}`;
  const candidates = new Set();
  if (sourceUrl) {
    candidates.add(getTypeItemId(sourceUrl, itemId));
  }
  [0, 1, 2, 3, 4, 9].forEach((t) => candidates.add(`${t}-${idStr}`));

  const merged = {};
  for (const key of Object.keys(store)) {
    const bag = store[key];
    if (!bag || typeof bag !== "object") continue;
    if (key === idStr || key.endsWith(suffix) || candidates.has(key)) {
      Object.assign(merged, bag);
    }
  }
  return merged;
}

/** 保存时：主 key + 兼容 key 一并写入，避免读侧 type 不一致丢锁 */
export function saveUserPriceLocksForProduct(sourceUrl, itemId, locks) {
  if (itemId == null || itemId === "") return;
  const primary = getTypeItemId(sourceUrl, itemId) || `1-${itemId}`;
  saveUserPriceLocks(primary, locks);
  // 淘宝/天猫常见备用 key
  const alt = `1-${itemId}`;
  if (alt !== primary && locks && Object.keys(locks).length) {
    const store = readStore();
    store[alt] = locks;
    writeStore(store);
  }
}

export function clearUserPriceLocks(typeItemId) {
  if (!typeItemId) return;
  const store = readStore();
  delete store[typeItemId];
  writeStore(store);
}

/** 批量清除（重读链接时） */
export function clearUserPriceLocksMany(typeItemIds) {
  if (!Array.isArray(typeItemIds) || typeItemIds.length === 0) return;
  const store = readStore();
  typeItemIds.forEach((id) => {
    delete store[id];
  });
  writeStore(store);
}

/** 合并本机用户锁到 skuMap（会改原对象） */
export function applyUserPriceLocks(skuMap, locks) {
  if (!skuMap || !locks) return skuMap;
  for (const key in locks) {
    if (!skuMap[key]) continue;
    const calcPrice = locks[key]?.calcPrice;
    if (calcPrice == null || calcPrice === "") continue;
    skuMap[key].calcPrice = String(calcPrice);
    skuMap[key].isLockPrice = true;
    skuMap[key].isUserLockPrice = true;
  }
  return skuMap;
}

/**
 * 生成可上传商品库的干净 source_json（深拷贝）
 * 去掉：用户价锁、引流预览锁、临时引流 SKU、calcPrice
 */
export function stripSourceJsonForRemote(sourceJson) {
  if (!sourceJson) return sourceJson;
  let cloned;
  try {
    cloned = JSON.parse(JSON.stringify(sourceJson));
  } catch (e) {
    return sourceJson;
  }
  // 去掉预览增加的引流规格
  const attrs = cloned?.sku?.attrs;
  const allAttrs = cloned?.sku?.allAttrs;
  const skuMap = cloned?.sku?.skuMap;
  const drainPropIds = new Set();
  if (attrs) {
    for (const dim in attrs) {
      const props = attrs[dim]?.props || {};
      for (const pid in props) {
        if (props[pid]?.isLowPriceDrainSku) {
          drainPropIds.add(pid);
          delete props[pid];
          if (allAttrs?.[pid]) delete allAttrs[pid];
        }
      }
    }
  }
  if (skuMap) {
    for (const id in skuMap) {
      const sku = skuMap[id];
      if (!sku) continue;
      if (sku.isLowPriceDrainSku) {
        delete skuMap[id];
        continue;
      }
      const parts = String(id).split(";").filter(Boolean);
      if (parts.some((p) => drainPropIds.has(p))) {
        delete skuMap[id];
        continue;
      }
      delete sku.isUserLockPrice;
      delete sku.isLowLockPrice;
      delete sku.isLowLockStock;
      delete sku.isLowPriceDrainSku;
      delete sku.jiSuanPrice;
      delete sku.sourceBasePrice;
      sku.isLockPrice = false;
      delete sku.calcPrice;
    }
  }
  return cloned;
}

export function getTypeItemId(sourceUrl, itemId) {
  if (itemId == null || itemId === "") return "";
  let type = 0;
  try {
    if (sourceUrl) {
      const u = new URL(sourceUrl);
      if (u.host.includes("1688")) type = 2;
      else if (u.host.includes("yangkeduo") || u.host.includes("pinduoduo")) type = 3;
      else if (u.host.includes("jinritemai")) type = 4;
      else if (u.host.includes("taobao") || u.host.includes("tmall")) type = 1;
      else type = 1;
    }
  } catch (e) {
    type = 1;
  }
  return `${type}-${itemId}`;
}

/** 与 product 缓存一致：ptype-itemId */
export function typeItemIdFromPtype(ptype, itemId) {
  if (itemId == null || itemId === "") return "";
  return `${ptype || 0}-${itemId}`;
}
